import { usePricing } from '@/hooks/usePricing';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { GAP_TITLES, GAP_NARRATIVES, GAP_HOMEWORK, buildMirrorCopy, TOKENS } from './glowUpGapNarratives';
import { trackLead, trackCustomEvent } from '@/utils/metaPixel';
import GlowUpGapVideoSection from './GlowUpGapVideoSection';

const API = `${window.location.origin}/api`;

/**
 * Glow Up Gap Assessment — standalone luxury lead-magnet quiz.
 * Route: /glow-up-gap
 * No nav, no footer, no shared chrome. Self-contained.
 *
 * Stages: 'landing' → 'q1' → 'q2' → 'q3' → 'q4' → 'q5' → 'results'
 * Mobile-first. Libre Baskerville (headlines) + DM Sans (body) loaded in index.html.
 */

// ──────────────────────────────────────────────────────────────────────
// Design tokens — sourced from shared module (see glowUpGapNarratives.js).
// Re-exported via import above so we keep this file's existing references.

// ──────────────────────────────────────────────────────────────────────
// Question definitions — pillars + answer scoring.
// A=4, B=3, C=2, D=1, E=0  (Q1 has 4 options: A-D = 4..1)
// ──────────────────────────────────────────────────────────────────────
// 12-question definitions (mixed formats): 10 pillar questions (2 per pillar)
// + 2 proof questions woven throughout (Q4, Q11 — NOT scored, answer text
// captured for the results-page "Mirror" section).
//
// Types:
//   'mc'      — multiple choice with per-option score
//   'rating'  — 1–10 slider with 3 anchor labels; score derived via scoreSlider()
//   'proof'   — multiple choice; answer letter+text stored, NOT scored
//
// Scoring map for sliders (per spec):
//   9–10 → 4 · 7–8 → 3 · 5–6 → 2 · 3–4 → 1 · 1–2 → 0
const scoreSlider = (v) => {
  const n = Number(v);
  if (n >= 9) return 4;
  if (n >= 7) return 3;
  if (n >= 5) return 2;
  if (n >= 3) return 1;
  return 0;
};

const QUESTIONS = [
  // Q1 — Clarity #1 (long-form)
  {
    type: 'mc',
    pillar: 'clarity',
    headline: 'Be honest with yourself for a moment.',
    body: "When you picture the woman you're becoming — how clearly can you actually see her on the hard days?",
    options: [
      { letter: 'A', score: 4, text: "She's crystal clear to me. I can access her vision even when everything feels hard." },
      { letter: 'B', score: 3, text: 'I know who she is, but I lose sight of her when life gets loud and overwhelming.' },
      { letter: 'C', score: 1, text: 'I have a general idea but it shifts depending on my mood and what\u2019s happening around me.' },
      { letter: 'D', score: 0, text: "Honestly? I just know I'm not her yet. The vision is blurry and I can't seem to hold it." },
    ],
  },
  // Q2 — Habits #1 (rating slider, format break)
  {
    type: 'rating',
    pillar: 'habits',
    headline: "Let's talk about consistency.",
    body: 'How consistently do you follow through on the habits you commit to?',
    anchors: {
      10: 'I stick to it because it matters — it becomes part of me',
      5: "I do well for 2\u20133 weeks, then fall off and don't find my way back",
      1: 'I rarely even get off the ground',
    },
  },
  // Q3 — Mindset #1 (long-form)
  {
    type: 'mc',
    pillar: 'mindset',
    headline: 'This one requires honesty.',
    body: "There's a version of you that has everything you want. What's the most truthful reason she hasn't fully shown up yet?",
    options: [
      { letter: 'A', score: 4, text: 'I keep waiting for the perfect moment, the right circumstances, the green light.' },
      { letter: 'B', score: 3, text: "I sabotage myself right when things start going well — like I'm not sure I'm allowed to have it." },
      { letter: 'C', score: 2, text: "Deep down I'm not sure I fully believe I deserve what I'm working toward." },
      { letter: 'D', score: 1, text: "I'm so depleted I have nothing left — I'm running on fumes and it shows." },
      { letter: 'E', score: 0, text: "I genuinely don't know. I just feel stuck and I can't explain why." },
    ],
  },
  // Q4 — PROOF #1 (commitment, NOT scored)
  {
    type: 'proof',
    proofKey: 'q4',
    pillar: null,
    headline: 'A quick gut check.',
    body: 'If someone with 20+ years of business growth experience handed you an exact daily plan — and told you precisely what to do each day to reach your most ambitious goal — would you actually follow it?',
    options: [
      { letter: 'A', text: 'Yes, in a heartbeat. Just tell me what to do.' },
      { letter: 'B', text: "Yes — but I'd need someone keeping me accountable." },
      { letter: 'C', text: "I'd want to, but I keep trying to figure it out alone instead." },
      { letter: 'D', text: "Probably not — I'd talk myself out of it." },
    ],
  },
  // Q5 — Strategy #1 (long-form)
  {
    type: 'mc',
    pillar: 'strategy',
    headline: "Now let's talk about your goals and your money.",
    body: 'When it comes to building the life and income you want — your business, your finances, your next level — where do you actually get stuck?',
    options: [
      { letter: 'A', score: 4, text: "I have too many goals and can't decide which one to focus on first." },
      { letter: 'B', score: 3, text: "I have a goal but I'm winging it — there's no real plan connecting today to where I want to be." },
      { letter: 'C', score: 2, text: "I have a plan but I'm not taking consistent action on it — I know what to do, I'm just not doing it." },
      { letter: 'D', score: 1, text: "I don't know what I should be doing to grow my income or reach my financial goals." },
      { letter: 'E', score: 0, text: "I make progress then plateau — I can't figure out why I keep hitting the same ceiling." },
    ],
  },
  // Q6 — Well-being #1 (rating slider)
  {
    type: 'rating',
    pillar: 'wellbeing',
    headline: "Let's check in on you.",
    body: 'How well are you taking care of the woman you are right now?',
    anchors: {
      10: 'I protect my energy, rest, and well-being without guilt',
      5: "I take care of myself when I remember — but it's last on the list",
      1: "I'm running on empty and I know it",
    },
  },
  // Q7 — Clarity #2 (short MC)
  {
    type: 'mc',
    pillar: 'clarity',
    headline: 'First thing in the morning…',
    body: 'When you wake up, do you know the ONE thing that matters most for your bigger vision that day?',
    options: [
      { letter: 'A', score: 4, text: 'Yes — almost every day.' },
      { letter: 'B', score: 3, text: "Some days, when I'm organized." },
      { letter: 'C', score: 1, text: "Rarely — I just react to whatever's loudest." },
      { letter: 'D', score: 0, text: "I don't have a clear bigger vision to anchor to." },
    ],
  },
  // Q8 — Habits #2 (short scenario)
  {
    type: 'mc',
    pillar: 'habits',
    headline: 'When the motivation fades…',
    body: 'The spark that got you started always wears off eventually. When it does, what usually happens?',
    options: [
      { letter: 'A', score: 4, text: "I keep going anyway — my systems carry me when motivation doesn't." },
      { letter: 'B', score: 3, text: 'I push through for a while, then it gets hit or miss.' },
      { letter: 'C', score: 1, text: "I drift off and tell myself I'll restart Monday." },
      { letter: 'D', score: 0, text: 'I stop completely — and then feel guilty about it.' },
    ],
  },
  // Q9 — Mindset #2 (rating slider)
  {
    type: 'rating',
    pillar: 'mindset',
    headline: 'Be honest here.',
    body: 'How often does a part of you pull back or sabotage things right when they start going well?',
    anchors: {
      10: 'Almost never — I let good things be good',
      5: 'Sometimes — I notice myself doing it',
      1: "Constantly — the moment it's working, I find a way to undo it",
    },
  },
  // Q10 — Strategy #2 (short MC)
  {
    type: 'mc',
    pillar: 'strategy',
    headline: 'Right now, the truth is…',
    body: 'When it comes to reaching your goals, which is most true today?',
    options: [
      { letter: 'A', score: 4, text: "I know exactly what to do each day — and I'm doing it." },
      { letter: 'B', score: 2, text: "I have a plan, but I'm not consistently acting on it." },
      { letter: 'C', score: 1, text: "I'm mostly guessing and hoping it adds up." },
      { letter: 'D', score: 0, text: "I don't really know what I should be doing." },
    ],
  },
  // Q11 — PROOF #2 (commitment statement, NOT scored)
  {
    type: 'proof',
    proofKey: 'q11',
    pillar: null,
    headline: 'Last gut check.',
    body: "How much does this land for you? \u201CI'm tired of trying to figure it all out alone — if someone just told me what to do each day, I'd actually do it.\u201D",
    options: [
      { letter: 'A', text: "That's exactly me." },
      { letter: 'B', text: 'Mostly true.' },
      { letter: 'C', text: 'Sometimes true.' },
      { letter: 'D', text: 'Not really me.' },
    ],
  },
  // Q12 — Well-being #2 (frequency)
  {
    type: 'mc',
    pillar: 'wellbeing',
    headline: 'One more.',
    body: 'Do you find yourself running on empty just to keep up with everything you\u2019re carrying?',
    options: [
      { letter: 'A', score: 4, text: 'No — I protect my energy, and it shows.' },
      { letter: 'B', score: 3, text: 'Sometimes, but I bounce back.' },
      { letter: 'C', score: 1, text: "Often — I'm usually depleted." },
      { letter: 'D', score: 0, text: "Constantly — I'm running on fumes." },
    ],
  },
];

// ──────────────────────────────────────────────────────────────────────
// Tiny shared UI primitives (scoped — no global side-effects).

const PageShell = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      background: TOKENS.bg,
      color: TOKENS.dark,
      fontFamily: TOKENS.sans,
      padding: '32px 20px 64px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      WebkitFontSmoothing: 'antialiased',
    }}
  >
    {children}
  </div>
);

const GoldDivider = ({ width = 60 }) => (
  <div
    style={{
      width: `${width}px`,
      height: '1px',
      background: TOKENS.gold,
      margin: '28px auto',
      opacity: 0.7,
    }}
  />
);

const TopLabel = ({ children }) => (
  <div
    style={{
      color: TOKENS.gold,
      fontSize: '11px',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      fontWeight: 600,
      textAlign: 'center',
      marginBottom: '32px',
    }}
    data-testid="glowup-top-label"
  >
    {children}
  </div>
);

// ──────────────────────────────────────────────────────────────────────
// Landing screen — name + email capture.

const Landing = ({ firstName, setFirstName, email, setEmail, onSubmit, error }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      style={{ width: '100%', maxWidth: 560 }}
    >
      <TopLabel>Designed by Deon Stokes · Founder of AmbitionGPT</TopLabel>

      <h1
        style={{
          fontFamily: TOKENS.serif,
          fontWeight: 400,
          fontSize: 'clamp(36px, 7vw, 52px)',
          lineHeight: 1.1,
          textAlign: 'center',
          margin: 0,
          color: TOKENS.dark,
        }}
        data-testid="glowup-landing-headline"
      >
        Find Your
        <br />
        <span style={{ color: TOKENS.gold }}>Glow Up Gap.</span>
      </h1>

      <p
        style={{
          fontFamily: TOKENS.sans,
          fontSize: '16px',
          lineHeight: 1.6,
          color: TOKENS.muted,
          textAlign: 'center',
          maxWidth: 420,
          margin: '24px auto 0',
        }}
      >
        You already know you&apos;re meant for more. This 2-minute assessment identifies the one
        thing standing between the woman you are and the woman you&apos;re becoming — so you can
        finally stop guessing and start moving.
      </p>

      <GoldDivider />

      <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
        {/* First name */}
        <label
          htmlFor="glowup-firstname"
          style={{
            display: 'block',
            fontSize: '13px',
            color: TOKENS.muted,
            marginBottom: '8px',
            fontFamily: TOKENS.sans,
          }}
        >
          What should Dea call you?
        </label>
        <input
          id="glowup-firstname"
          type="text"
          autoComplete="given-name"
          autoCapitalize="words"
          placeholder="Your first name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          data-testid="glowup-firstname-input"
          style={{
            width: '100%',
            padding: '16px 18px',
            fontSize: '16px',
            border: `1px solid ${TOKENS.cardBorder}`,
            borderRadius: '12px',
            background: TOKENS.cardBg,
            color: TOKENS.dark,
            fontFamily: TOKENS.sans,
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 200ms ease',
          }}
          onFocus={(e) => (e.target.style.borderColor = TOKENS.gold)}
          onBlur={(e) => (e.target.style.borderColor = TOKENS.cardBorder)}
        />

        {/* Email */}
        <label
          htmlFor="glowup-email"
          style={{
            display: 'block',
            fontSize: '13px',
            color: TOKENS.muted,
            margin: '20px 0 8px',
            fontFamily: TOKENS.sans,
          }}
        >
          Where should Dea send your personalized Glow Up Gap Report?
        </label>
        <input
          id="glowup-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="glowup-email-input"
          style={{
            width: '100%',
            padding: '16px 18px',
            fontSize: '16px',
            border: `1px solid ${TOKENS.cardBorder}`,
            borderRadius: '12px',
            background: TOKENS.cardBg,
            color: TOKENS.dark,
            fontFamily: TOKENS.sans,
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 200ms ease',
          }}
          onFocus={(e) => (e.target.style.borderColor = TOKENS.gold)}
          onBlur={(e) => (e.target.style.borderColor = TOKENS.cardBorder)}
        />

        {error && (
          <p
            style={{ color: '#B23A48', fontSize: '13px', marginTop: '12px', textAlign: 'center' }}
            data-testid="glowup-landing-error"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          data-testid="glowup-landing-cta"
          style={{
            marginTop: '28px',
            width: '100%',
            background: TOKENS.gold,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '30px',
            padding: '18px 24px',
            fontSize: '16px',
            fontWeight: 600,
            fontFamily: TOKENS.sans,
            cursor: 'pointer',
            letterSpacing: '0.01em',
            transition: 'transform 150ms ease, box-shadow 200ms ease',
            boxShadow: '0 1px 2px rgba(61, 50, 41, 0.08)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(184, 149, 106, 0.28)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(61, 50, 41, 0.08)';
          }}
        >
          Show Me My Gap →
        </button>

        <p
          style={{
            fontSize: '11px',
            color: TOKENS.muted,
            textAlign: 'center',
            marginTop: '16px',
            letterSpacing: '0.02em',
          }}
        >
          Private and personalized. No spam — just your truth.
        </p>
      </form>
    </motion.div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Rating slider — used for question type 'rating' (1–10 scale with 3 anchor labels).
// Returns numeric value via onChange; null = not yet interacted.

const RatingSlider = ({ value, onChange, anchors, testidPrefix }) => {
  // Untouched state per v3 spec: thumb pinned far-left (position 1), grayed,
  // readout shows "—", microcopy prompts interaction. First slider touch commits
  // the value and gates the "Next" button.
  const hasValue = value !== null && value !== undefined;
  const v = hasValue ? value : 1; // visual position when untouched
  const fillPct = hasValue ? ((v - 1) / 9) * 100 : 0;
  const thumbColor = hasValue ? TOKENS.gold : '#C8C0B5';

  return (
    <div style={{ width: '100%' }} data-testid={`${testidPrefix}-rating`}>
      {/* Large readout */}
      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <span
          style={{
            fontFamily: TOKENS.serif,
            fontSize: 'clamp(40px, 9vw, 56px)',
            color: hasValue ? TOKENS.gold : TOKENS.cardBorder,
            fontWeight: 400,
            lineHeight: 1,
            transition: 'color 200ms ease',
          }}
          data-testid={`${testidPrefix}-rating-value`}
        >
          {hasValue ? v : '\u2014'}
        </span>
        <span style={{ color: TOKENS.muted, fontSize: '13px', marginLeft: 8 }}>/ 10</span>
      </div>

      {!hasValue && (
        <p
          style={{
            textAlign: 'center',
            color: TOKENS.muted,
            fontSize: '12px',
            fontStyle: 'italic',
            margin: '0 0 4px 0',
            letterSpacing: '0.01em',
          }}
          data-testid={`${testidPrefix}-rating-prompt`}
        >
          Move the slider to choose your number.
        </p>
      )}

      {/* Slider */}
      <div style={{ padding: '12px 4px 4px' }}>
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={v}
          onChange={(e) => onChange(Number(e.target.value))}
          onTouchStart={() => {
            if (!hasValue) onChange(v);
          }}
          data-testid={`${testidPrefix}-rating-slider`}
          aria-label={`Rate from 1 to 10`}
          style={{
            width: '100%',
            appearance: 'none',
            WebkitAppearance: 'none',
            background: `linear-gradient(to right, ${TOKENS.gold} 0%, ${TOKENS.gold} ${fillPct}%, ${TOKENS.cardBorder} ${fillPct}%, ${TOKENS.cardBorder} 100%)`,
            height: '6px',
            borderRadius: '3px',
            outline: 'none',
            cursor: 'pointer',
          }}
        />
        <style>{`
          input[type=range][data-testid="${testidPrefix}-rating-slider"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: ${thumbColor};
            border: 3px solid #FFFFFF;
            box-shadow: 0 2px 8px ${hasValue ? 'rgba(184,149,106,0.35)' : 'rgba(0,0,0,0.10)'};
            cursor: pointer;
          }
          input[type=range][data-testid="${testidPrefix}-rating-slider"]::-moz-range-thumb {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: ${thumbColor};
            border: 3px solid #FFFFFF;
            box-shadow: 0 2px 8px ${hasValue ? 'rgba(184,149,106,0.35)' : 'rgba(0,0,0,0.10)'};
            cursor: pointer;
          }
        `}</style>
      </div>

      {/* Anchor labels (1 / 5 / 10) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '8px',
          marginTop: '14px',
        }}
      >
        {[1, 5, 10].map((n, i) => (
          <div
            key={n}
            style={{
              textAlign: i === 0 ? 'left' : i === 1 ? 'center' : 'right',
            }}
          >
            <span
              style={{
                display: 'block',
                fontSize: '11px',
                color: TOKENS.gold,
                fontWeight: 700,
                marginBottom: '4px',
                letterSpacing: '0.04em',
              }}
            >
              {n}
            </span>
            <span
              style={{
                display: 'block',
                fontSize: '12px',
                color: TOKENS.muted,
                lineHeight: 1.4,
                fontStyle: 'italic',
              }}
            >
              &ldquo;{anchors[n]}&rdquo;
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Question screen — renders mc / rating / proof types.

const QuestionScreen = ({ qIndex, question, answer, onAnswer, onBack, onNext, isLast, error }) => {
  const total = QUESTIONS.length;
  const hasAnswer =
    answer !== null && answer !== undefined && (question.type !== 'rating' || answer.value != null);
  const progress = ((qIndex + (hasAnswer ? 1 : 0)) / total) * 100;

  return (
    <motion.div
      key={`q-${qIndex}`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{ width: '100%', maxWidth: 600 }}
    >
      {/* Progress + back */}
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            width: '100%',
            height: '3px',
            background: TOKENS.cardBorder,
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background: TOKENS.gold,
              transition: 'width 300ms ease',
            }}
            data-testid="glowup-progress-bar"
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '10px',
          }}
        >
          <button
            type="button"
            onClick={onBack}
            data-testid="glowup-back-btn"
            style={{
              background: 'transparent',
              border: 'none',
              color: TOKENS.muted,
              fontSize: '13px',
              fontFamily: TOKENS.sans,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: 0,
            }}
          >
            <ArrowLeft size={14} /> Back
          </button>
          <span
            style={{
              fontSize: '11px',
              letterSpacing: '0.16em',
              color: TOKENS.muted,
              textTransform: 'uppercase',
            }}
            data-testid="glowup-progress-label"
          >
            Question {qIndex + 1} of {total}
          </span>
        </div>
      </div>

      {/* Headline */}
      <h2
        style={{
          fontFamily: TOKENS.serif,
          fontSize: 'clamp(20px, 4.4vw, 24px)',
          lineHeight: 1.25,
          color: TOKENS.dark,
          textAlign: 'center',
          margin: '24px 0 12px',
          fontWeight: 400,
        }}
        data-testid="glowup-question-headline"
      >
        {question.headline}
      </h2>

      {/* Body */}
      <p
        style={{
          fontFamily: TOKENS.sans,
          fontSize: '16px',
          lineHeight: 1.55,
          color: TOKENS.dark,
          textAlign: 'center',
          maxWidth: 460,
          margin: '0 auto 28px',
        }}
        data-testid="glowup-question-body"
      >
        {question.body}
      </p>

      {/* Answer interface — varies by type */}
      {question.type === 'rating' ? (
        <RatingSlider
          value={answer?.value ?? null}
          onChange={(val) => onAnswer({ type: 'rating', value: val })}
          anchors={question.anchors}
          testidPrefix={`glowup-q${qIndex + 1}`}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {question.options.map((opt, idx) => {
            const isSelected = answer?.optionIdx === idx;
            const pillarOrProof = question.pillar || (question.type === 'proof' ? question.proofKey : 'q');
            return (
              <button
                key={opt.letter}
                type="button"
                onClick={() =>
                  onAnswer({
                    type: question.type,
                    optionIdx: idx,
                    letter: opt.letter,
                    text: opt.text,
                  })
                }
                data-testid={`glowup-answer-${pillarOrProof}-${opt.letter}`}
                style={{
                  width: '100%',
                  minHeight: '56px',
                  padding: '16px 18px',
                  background: isSelected ? TOKENS.goldSoft : TOKENS.cardBg,
                  border: isSelected ? `2px solid ${TOKENS.gold}` : `1px solid ${TOKENS.cardBorder}`,
                  borderRadius: '14px',
                  textAlign: 'left',
                  fontFamily: TOKENS.sans,
                  fontSize: '15px',
                  lineHeight: 1.5,
                  color: TOKENS.dark,
                  cursor: 'pointer',
                  transition: 'border-color 180ms ease, background 180ms ease, transform 120ms ease',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  boxSizing: 'border-box',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = TOKENS.gold;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = TOKENS.cardBorder;
                }}
              >
                <span
                  style={{
                    color: TOKENS.gold,
                    fontWeight: 700,
                    fontSize: '13px',
                    letterSpacing: '0.04em',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}
                >
                  {opt.letter}.
                </span>
                <span style={{ flex: 1 }}>{opt.text}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Next button */}
      <AnimatePresence>
        {hasAnswer && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ marginTop: '24px' }}
          >
            <button
              type="button"
              onClick={onNext}
              data-testid="glowup-next-btn"
              style={{
                width: '100%',
                background: TOKENS.gold,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '30px',
                padding: '18px 24px',
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: TOKENS.sans,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                letterSpacing: '0.01em',
              }}
            >
              {isLast ? 'See My Results →' : (
                <>
                  Next <ArrowRight size={16} />
                </>
              )}
            </button>
            {error && (
              <p
                style={{ color: '#B23A48', fontSize: '13px', marginTop: '12px', textAlign: 'center' }}
                data-testid="glowup-question-error"
              >
                {error}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Helper: compute per-pillar scores from answers + question definitions.
// ──────────────────────────────────────────────────────────────────────
// Computes per-pillar scores (sum of 2 questions, range 0–8) and extracts
// proof answers (Q4, Q11) from the in-flight answer state.
//
// answers[i] shape:
//   mc/proof: { type, optionIdx, letter, text }
//   rating:   { type: 'rating', value: 1..10 }
const computeScoresAndProofs = (answers) => {
  const scores = { clarity: 0, habits: 0, mindset: 0, strategy: 0, wellbeing: 0 };
  const proof = {}; // { q4: 'A', q11: 'B' } (letters)
  QUESTIONS.forEach((q, idx) => {
    const a = answers[idx];
    if (a == null) return;
    if (q.type === 'mc' && q.pillar) {
      const opt = q.options[a.optionIdx];
      if (opt) scores[q.pillar] += opt.score;
    } else if (q.type === 'rating' && q.pillar) {
      scores[q.pillar] += scoreSlider(a.value);
    } else if (q.type === 'proof' && q.proofKey) {
      proof[q.proofKey] = a.letter;
    }
  });
  return { scores, proof };
};

// ──────────────────────────────────────────────────────────────────────
// Results screen — fired AFTER /api/glow-up-gap/submit succeeds.

const ResultsScreen = ({ firstName, primaryGap, secondaryGap, reportId, email, proofQ4, proofQ11 }) => {
  const pricing = usePricing();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const handleStartTrial = async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      // Redirect to our branded inline checkout with the prefilled context.
      // The PaymentIntent is created on /checkout via /api/payment/intent/create
      // (access_type=glow_up_trial) and the $37/mo subscription is finalized
      // automatically after successful payment.
      const params = new URLSearchParams({
        plan: 'glow-up-trial',
        email: email || '',
        first_name: firstName || '',
        report_id: reportId || '',
      });
      window.location.href = `/checkout?${params.toString()}`;
    } catch (err) {
      console.error(err);
      setCheckoutError("Couldn't start checkout. Please try again in a moment.");
      setCheckoutLoading(false);
    }
  };

  const paragraphs = GAP_NARRATIVES[primaryGap] || [];
  const primaryTitle = GAP_TITLES[primaryGap] || '';
  const secondaryTitle = secondaryGap ? GAP_TITLES[secondaryGap] : null;

  return (
    <motion.div
      key="results"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{ width: '100%', maxWidth: 620 }}
    >
      <TopLabel data-testid="glowup-results-top-label">
        {firstName ? `${firstName}, Your` : 'Your'} Glow Up Gap
      </TopLabel>

      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <h1
          style={{
            fontFamily: TOKENS.serif,
            fontSize: 'clamp(36px, 8vw, 56px)',
            fontWeight: 400,
            color: TOKENS.dark,
            margin: 0,
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
          }}
          data-testid="glowup-results-title"
        >
          {primaryTitle}
        </h1>
        {/* Animated gold underline */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: '80px', opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.25, ease: 'easeOut' }}
          style={{
            height: '2px',
            background: TOKENS.gold,
            margin: '14px auto 0',
          }}
        />
      </div>

      {secondaryTitle && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          style={{
            marginTop: '24px',
            fontSize: '14px',
            fontStyle: 'italic',
            color: TOKENS.muted,
            textAlign: 'center',
            fontFamily: TOKENS.sans,
          }}
          data-testid="glowup-secondary-gap"
        >
          You may also be experiencing a secondary gap in {secondaryTitle}.
        </motion.p>
      )}

      <div style={{ marginTop: '36px' }}>
        {paragraphs.map((p, i) => (
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
            style={{
              fontFamily: TOKENS.sans,
              fontSize: '16px',
              lineHeight: 1.65,
              color: TOKENS.dark,
              margin: '0 0 18px 0',
            }}
          >
            {p}
          </motion.p>
        ))}
      </div>

      {/* PERSONALIZED VIDEO MESSAGE — one per primary_gap.
          Sits between the narrative and the "Start Today" homework card per spec. */}
      <GlowUpGapVideoSection primaryGap={primaryGap} tokens={TOKENS} reportId={reportId} />

      {/* HOMEWORK CARD — START TODAY · YOUR ONE MOVE (v3) */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        style={{
          background: TOKENS.cardBg,
          border: `1px solid ${TOKENS.cardBorder}`,
          borderLeft: `3px solid ${TOKENS.gold}`,
          borderRadius: '12px',
          padding: '24px',
          margin: '28px 0 24px',
        }}
        data-testid="glowup-homework-card"
      >
        <p
          style={{
            color: TOKENS.gold,
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            fontWeight: 600,
            margin: '0 0 12px 0',
          }}
        >
          Start Today — Your One Move
        </p>
        <p
          style={{
            fontSize: '13px',
            lineHeight: 1.7,
            color: TOKENS.muted,
            margin: '0 0 14px 0',
            fontFamily: TOKENS.sans,
          }}
        >
          Before you do anything else, here&apos;s one small thing you can do right now to start closing your {primaryTitle} gap:
        </p>
        <p
          style={{
            fontSize: '15px',
            lineHeight: 1.7,
            color: TOKENS.dark,
            margin: '0 0 14px 0',
            fontFamily: TOKENS.sans,
          }}
          data-testid="glowup-homework-body"
        >
          {GAP_HOMEWORK[primaryGap] || ''}
        </p>
        <p
          style={{
            fontSize: '13px',
            lineHeight: 1.7,
            color: TOKENS.muted,
            margin: 0,
            fontFamily: TOKENS.sans,
            fontStyle: 'italic',
          }}
        >
          That&apos;s a taste of how Dea works — one doable move at a time. Imagine 90 days of this, built around you.
        </p>
      </motion.div>

      <GoldDivider width={80} />

      {/* "Inside Your 90-Day Journey" card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.4 }}
        style={{
          background: TOKENS.cardBg,
          border: `1px solid ${TOKENS.cardBorder}`,
          borderLeft: `3px solid ${TOKENS.gold}`,
          borderRadius: '12px',
          padding: '24px',
          margin: '8px 0 32px',
        }}
        data-testid="glowup-90day-card"
      >
        <p
          style={{
            color: TOKENS.gold,
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            fontWeight: 600,
            margin: '0 0 12px 0',
          }}
        >
          Inside Your 90-Day Journey
        </p>
        <p
          style={{
            fontSize: '13px',
            lineHeight: 1.7,
            color: TOKENS.muted,
            margin: '0 0 12px 0',
            fontFamily: TOKENS.sans,
          }}
        >
          Your personalized roadmap. Your daily check-ins. Your habit tracker. Your journal. Your milestone system. A coach who knows exactly where you are in your journey — every single day.
        </p>
        <p
          style={{
            fontSize: '13px',
            lineHeight: 1.7,
            color: TOKENS.muted,
            margin: '0 0 8px 0',
            fontFamily: TOKENS.sans,
          }}
        >
          This isn&apos;t another chatbot.
        </p>
        <p
          style={{
            fontSize: '13px',
            lineHeight: 1.7,
            color: TOKENS.muted,
            margin: 0,
            fontFamily: TOKENS.sans,
          }}
        >
          It&apos;s a personalized system built around your goals, your habits, your season of life, and the woman you&apos;re becoming.
        </p>
      </motion.div>

      {/* CTA */}
      <div style={{ textAlign: 'center', marginTop: '8px' }}>
        {(() => {
          const mirror = buildMirrorCopy(proofQ4, proofQ11);
          if (!mirror) return null;
          return (
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.45 }}
              style={{
                fontFamily: TOKENS.sans,
                fontSize: '15px',
                lineHeight: 1.65,
                color: TOKENS.dark,
                margin: '0 auto 22px',
                maxWidth: '480px',
                textAlign: 'left',
              }}
              data-testid="glowup-mirror"
            >
              {mirror}
            </motion.p>
          );
        })()}
        <h3
          style={{
            fontFamily: TOKENS.serif,
            fontSize: 'clamp(18px, 4vw, 22px)',
            fontWeight: 400,
            color: TOKENS.dark,
            margin: '0 0 10px 0',
            lineHeight: 1.3,
          }}
        >
          Your next 90 days don&apos;t have to be a guessing game.
        </h3>
        <p
          style={{
            fontFamily: TOKENS.sans,
            fontSize: '14px',
            color: TOKENS.muted,
            margin: '0 0 24px 0',
          }}
        >
          Start your 7-day trial with Dea for just $9.
        </p>

        <div
          style={{
            margin: '0 0 20px 0',
          }}
          data-testid="glowup-email-confirmation"
        >
          <p
            style={{
              fontSize: '12px',
              color: TOKENS.muted,
              margin: '0 0 6px 0',
              fontStyle: 'italic',
            }}
          >
            Your full Gap Report is also on its way to your inbox. ✓
          </p>
          <p
            style={{
              fontSize: '11px',
              color: TOKENS.muted,
              margin: 0,
              lineHeight: 1.6,
              maxWidth: '440px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
            data-testid="glowup-spam-nudge"
          >
            Don&apos;t see it in 2 minutes? Check your spam or promotions folder — and add{' '}
            <strong style={{ color: TOKENS.dark, fontWeight: 600 }}>hello@beambitious.chat</strong>{' '}
            to your contacts so future emails land in your primary inbox.
          </p>
        </div>

        <button
          type="button"
          onClick={handleStartTrial}
          disabled={checkoutLoading}
          data-testid="glowup-trial-cta"
          style={{
            width: '100%',
            background: TOKENS.gold,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '30px',
            padding: '18px 24px',
            fontSize: '16px',
            fontWeight: 700,
            fontFamily: TOKENS.sans,
            cursor: checkoutLoading ? 'not-allowed' : 'pointer',
            opacity: checkoutLoading ? 0.75 : 1,
            letterSpacing: '0.01em',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 1px 2px rgba(61, 50, 41, 0.08)',
            transition: 'transform 150ms ease, box-shadow 200ms ease',
          }}
          onMouseEnter={(e) => {
            if (!checkoutLoading) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(184, 149, 106, 0.28)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(61, 50, 41, 0.08)';
          }}
        >
          {checkoutLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Starting checkout…
            </>
          ) : (
            <>Start My 7-Day Trial — $9 →</>
          )}
        </button>
            <button
              type="button"
              onClick={() => { window.location.href = `/checkout?plan=lifetime&email=${encodeURIComponent(email || '')}&first_name=${encodeURIComponent(firstName || '')}`; }}
              data-testid="gap-lifetime-cta"
              style={{ width: '100%', marginTop: '12px', background: 'transparent', color: TOKENS.gold, border: `1.5px solid ${TOKENS.gold}`, borderRadius: '30px', padding: '16px 24px', fontSize: '15px', fontWeight: 600, fontFamily: TOKENS.sans, cursor: 'pointer' }}
            >
              Ready now? Get Lifetime Access — ${pricing.lifetime_price} one-time{pricing.is_early_access ? ` (normally $${pricing.lifetime_regular_price})` : ''}
            </button>


        {checkoutError && (
          <p
            style={{ color: '#B23A48', fontSize: '13px', marginTop: '12px' }}
            data-testid="glowup-checkout-error"
          >
            {checkoutError}
          </p>
        )}

        <p
          style={{
            fontSize: '11px',
            color: TOKENS.muted,
            marginTop: '14px',
            lineHeight: 1.6,
            maxWidth: '460px',
            margin: '14px auto 0',
          }}
        >
          Your $9 starts your 7 days with Dea. Nothing is charged automatically after that — ever. If you want to keep her, you can unlock lifetime access yourself for ${pricing.upgrade_price} (your $9 applies).
        </p>
      </div>
    </motion.div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Main page component — orchestrates stages.

export default function GlowUpGap() {
  const [stage, setStage] = useState('landing'); // 'landing' | qIndex (0-4) | 'submitting' | 'results'
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [landingError, setLandingError] = useState(null);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null); // { report_id, primary_gap, secondary_gap }

  // Set document title for premium feel
  useEffect(() => {
    const prev = document.title;
    document.title = 'Find Your Glow Up Gap | AmbitionGPT';
    return () => {
      document.title = prev;
    };
  }, []);

  const handleLandingSubmit = () => {
    const trimmedName = firstName.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      setLandingError('Please tell Dea your first name.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(trimmedEmail)) {
      setLandingError('Please enter a valid email address.');
      return;
    }
    setLandingError(null);
    setFirstName(trimmedName);
    setEmail(trimmedEmail);
    setStage(0);
  };

  const handleAnswer = (qIdx, answer) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[qIdx] = answer;
      return next;
    });
  };

  const submitAssessment = async () => {
    setStage('submitting');
    setSubmitError(null);
    try {
      const { scores, proof } = computeScoresAndProofs(answers);
      const resp = await fetch(`${API}/glow-up-gap/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          email,
          scores,
          proof_q4: proof.q4 || null,
          proof_q11: proof.q11 || null,
          origin_url: window.location.origin,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200)}`);
      }
      const data = await resp.json();
      setResult(data);
      setStage('results');

      // Meta Pixel tracking — fires ONCE per successful assessment submission.
      // - `Lead` (standard event) lets Meta optimize ad delivery toward similar qualified leads.
      // - `GlowUpGapComplete` (custom event) is the dedicated conversion event for funnel analytics.
      try {
        trackLead({
          content_name: 'Glow Up Gap Assessment',
          content_category: 'AI Coaching Lead Magnet',
          value: 0,
          currency: 'USD',
        });
        trackCustomEvent('GlowUpGapComplete', {
          content_name: 'Glow Up Gap Assessment',
          report_id: data?.report_id || null,
          primary_gap: data?.primary_gap || null,
          secondary_gap: data?.secondary_gap || null,
        });
      } catch (pixelErr) {
        // Never let analytics failures break the user flow.
        console.warn('Meta Pixel tracking skipped:', pixelErr);
      }

      // Scroll to top so the user sees the headline first
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (err) {
      console.error('Glow Up Gap submit failed:', err);
      setSubmitError("Hmm, something didn't go through. Please try again.");
      // Roll back to last question so they can retry
      setStage(QUESTIONS.length - 1);
    }
  };

  const handleNext = () => {
    if (typeof stage === 'number') {
      if (stage === QUESTIONS.length - 1) {
        submitAssessment();
      } else {
        setStage(stage + 1);
      }
    }
  };

  const handleBack = () => {
    if (typeof stage === 'number') {
      if (stage === 0) {
        setStage('landing');
      } else {
        setStage(stage - 1);
      }
    }
  };

  return (
    <PageShell>
      <AnimatePresence mode="wait">
        {stage === 'landing' && (
          <Landing
            key="landing"
            firstName={firstName}
            setFirstName={setFirstName}
            email={email}
            setEmail={setEmail}
            onSubmit={handleLandingSubmit}
            error={landingError}
          />
        )}

        {typeof stage === 'number' && (
          <QuestionScreen
            key={`q-${stage}`}
            qIndex={stage}
            question={QUESTIONS[stage]}
            answer={answers[stage]}
            onAnswer={(a) => handleAnswer(stage, a)}
            onBack={handleBack}
            onNext={handleNext}
            isLast={stage === QUESTIONS.length - 1}
            error={stage === QUESTIONS.length - 1 ? submitError : null}
          />
        )}

        {stage === 'submitting' && (
          <motion.div
            key="submitting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              textAlign: 'center',
              maxWidth: 420,
              padding: '60px 0',
            }}
          >
            <Loader2
              size={32}
              style={{ color: TOKENS.gold, animation: 'spin 1s linear infinite', margin: '0 auto 20px' }}
            />
            <p style={{ color: TOKENS.muted, fontSize: '14px', fontFamily: TOKENS.sans, letterSpacing: '0.04em' }}>
              Reading your truth...
            </p>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </motion.div>
        )}

        {stage === 'results' && result && (
          <ResultsScreen
            key="results"
            firstName={firstName}
            primaryGap={result.primary_gap}
            secondaryGap={result.secondary_gap}
            reportId={result.report_id}
            email={email}
            proofQ4={answers[3]?.letter || null}
            proofQ11={answers[10]?.letter || null}
          />
        )}
      </AnimatePresence>
    </PageShell>
  );
}
